import axios from "axios";

const API_URL = import.meta.env.VITE_API_BASE_URL || "/api";

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Configurable storage accessor helpers
export const tokenStorage = {
  getAccessToken(): string | null {
    return localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
  },
  getRefreshToken(): string | null {
    return localStorage.getItem("refreshToken") || sessionStorage.getItem("refreshToken");
  },
  setTokens(access: string, refresh: string, rememberMe: boolean = true) {
    if (rememberMe) {
      localStorage.setItem("accessToken", access);
      localStorage.setItem("refreshToken", refresh);
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
    } else {
      sessionStorage.setItem("accessToken", access);
      sessionStorage.setItem("refreshToken", refresh);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    }
  },
  clearTokens() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  },
};

// Request Interceptor: Attach Access Token & Tenant ID
api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem("tenantId") || localStorage.getItem("tenant_id");
  if (tenantId && config.headers) {
    config.headers["X-Tenant-ID"] = tenantId;
  }
  return config;
});

// Response Interceptor: 401 Silent Token Refresh & Retry
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        tokenStorage.clearTokens();
        window.dispatchEvent(new Event("auth:unauthorized"));
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}/auth/token/refresh/`, { refresh: refreshToken });
        const newAccess = response.data.access;
        const newRefresh = response.data.refresh || refreshToken;

        tokenStorage.setTokens(newAccess, newRefresh, !!localStorage.getItem("accessToken"));
        api.defaults.headers.common["Authorization"] = `Bearer ${newAccess}`;
        originalRequest.headers["Authorization"] = `Bearer ${newAccess}`;

        processQueue(null, newAccess);
        return api(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        tokenStorage.clearTokens();
        window.dispatchEvent(new Event("auth:unauthorized"));
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async getSetupStatus() {
    const res = await api.get("/auth/setup/");
    return res.data as { initialized: boolean; institution_name?: string };
  },

  async setup(data: Record<string, any>) {
    const res = await api.post("/auth/setup/", data);
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async register(data: Record<string, any>) {
    const res = await api.post("/auth/register/", data);
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async startRegisterSession(data: Record<string, any>) {
    const res = await api.post("/auth/register/session/", data);
    return res.data;
  },

  async verifyRegisterEmailOTP(sessionId: string, otpCode: string) {
    const res = await api.post("/auth/register/verify-email-otp/", { session_id: sessionId, otp_code: otpCode });
    return res.data;
  },

  async verifyRegisterPhoneOTP(sessionId: string, otpCode: string) {
    const res = await api.post("/auth/register/verify-phone-otp/", { session_id: sessionId, otp_code: otpCode });
    return res.data;
  },

  async completeRegisterSession(sessionId: string) {
    const res = await api.post("/auth/register/complete/", { session_id: sessionId });
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async login(data: { email?: string; username?: string; password?: string; rememberMe?: boolean }) {
    const { rememberMe = true, ...payload } = data;
    try {
      const res = await api.post("/auth/login/", payload);
      if (res.data.tokens?.access || res.data.access) {
        const access = res.data.tokens?.access || res.data.access;
        const refresh = res.data.tokens?.refresh || res.data.refresh || "";
        tokenStorage.setTokens(access, refresh, rememberMe);
      }
      return res.data;
    } catch (err: any) {
      throw err;
    }
  },

  async logout() {
    const refresh = tokenStorage.getRefreshToken();
    try {
      if (refresh) {
        await api.post("/auth/logout/", { refresh });
      }
    } catch {
      // Silent catch on network error during logout
    } finally {
      tokenStorage.clearTokens();
      window.dispatchEvent(new Event("auth:unauthorized"));
    }
  },

  async refreshToken() {
    const refresh = tokenStorage.getRefreshToken();
    if (!refresh) throw new Error("No refresh token available");
    const res = await axios.post(`${API_URL}/auth/token/refresh/`, { refresh });
    if (res.data.access) {
      tokenStorage.setTokens(res.data.access, res.data.refresh || refresh);
    }
    return res.data;
  },

  async getProfile() {
    const res = await api.get("/auth/profile/");
    return res.data;
  },

  async updateProfile(data: Record<string, any>) {
    const res = await api.patch("/auth/profile/", data);
    return res.data;
  },

  async changePassword(data: Record<string, any>) {
    const res = await api.post("/auth/change-password/", data);
    return res.data;
  },

  async forgotPassword(email: string) {
    const res = await api.post("/auth/forgot-password/", { email });
    return res.data;
  },

  async resetPassword(data: Record<string, any>) {
    const res = await api.post("/auth/reset-password/", data);
    return res.data;
  },

  async verifyEmail(token: string) {
    const res = await api.post("/auth/verify-email/", { token });
    return res.data;
  },

  async resendVerification(email: string) {
    const res = await api.post("/auth/resend-verification/", { email });
    return res.data;
  },

  async requestOTP(email: string) {
    const res = await api.post("/auth/verify-otp/", { email, action: "request" });
    return res.data;
  },

  async verifyOTP(email: string, otpCode: string) {
    const res = await api.post("/auth/verify-otp/", { email, otp_code: otpCode, action: "verify" });
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async loginWithGoogle(idToken: string) {
    const res = await api.post("/auth/google/", { id_token: idToken });
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async forceChangePassword(data: { old_password: string; new_password: string }) {
    const res = await api.post("/auth/force-change-password/", data);
    if (res.data.tokens?.access) {
      tokenStorage.setTokens(res.data.tokens.access, res.data.tokens.refresh);
    }
    return res.data;
  },

  async adminResetPassword(userId: string, customPassword?: string) {
    const payload: { user_id: string; custom_password?: string } = { user_id: userId };
    if (customPassword) {
      payload.custom_password = customPassword;
    }
    const res = await api.post("/auth/admin/reset-password/", payload);
    return res.data;
  },

  async adminUnlockAccount(userId: string) {
    const res = await api.post("/auth/admin/unlock-account/", { user_id: userId });
    return res.data;
  },

  async getPasswordStatus(userId?: string) {
    const url = userId ? `/auth/admin/password-status/${userId}/` : "/auth/admin/password-status/me/";
    const res = await api.get(url);
    return res.data;
  },
};


